import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronRight } from 'lucide-react'
import { Button } from '../Button'
import { Card, CardBody } from '../Card'
import { MARK_STYLE, formatPoints, useMarkGlyph } from './marks'
import { markPoints, pointsOver, type ScorecardClimber, type ScorecardRoute } from './types'

/** Cards drawn at once. A category of 60 is 60 grids of tiles. */
const PAGE = 30

interface Props {
  scoringType: string
  routes: ScorecardRoute[]
  climbers: ScorecardClimber[]
  /** The selected round, or null when every route is showing. */
  roundName: string | null
  /** Pinned above the list, so a climber never has to hunt for themselves. */
  me?: ScorecardClimber
  focus: { userId: string; tick: number } | null
  renderClimber: (climber: ScorecardClimber) => ReactNode
  onOpenClimber?: (climber: ScorecardClimber) => void
  onOpenRoute: (routeId: string) => void
}

export function ScorecardCards({
  scoringType,
  routes,
  climbers,
  roundName,
  me,
  focus,
  renderClimber,
  onOpenClimber,
  onOpenRoute,
}: Props) {
  const { t } = useTranslation()
  const [shown, setShown] = useState(PAGE)
  const [open, setOpen] = useState<Set<string>>(new Set())

  // "Me": make sure the card is drawn, open its sum, and bring it into view.
  useEffect(() => {
    if (!focus) return
    const index = climbers.findIndex((c) => c.userId === focus.userId)
    if (index === -1) return
    setShown((n) => Math.max(n, index + 1))
    setOpen((prev) => new Set(prev).add(focus.userId))
    requestAnimationFrame(() =>
      document
        .getElementById(`scorecard-${focus.userId}`)
        ?.scrollIntoView?.({ block: 'center', behavior: 'smooth' }),
    )
  }, [focus, climbers])

  function toggle(key: string) {
    setOpen((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const card = (climber: ScorecardClimber, pinned = false) => (
    <ClimberCard
      key={pinned ? `pinned-${climber.userId}` : climber.userId}
      id={pinned ? undefined : `scorecard-${climber.userId}`}
      climber={climber}
      scoringType={scoringType}
      routes={routes}
      roundName={roundName}
      mine={climber.userId === me?.userId}
      isOpen={open.has(pinned ? `pinned-${climber.userId}` : climber.userId)}
      onToggle={() => toggle(pinned ? `pinned-${climber.userId}` : climber.userId)}
      renderClimber={renderClimber}
      onOpenClimber={onOpenClimber}
      onOpenRoute={onOpenRoute}
    />
  )

  return (
    <div className="flex flex-col gap-2.5">
      {me && (
        <>
          <p className="px-1 text-[11px] font-bold uppercase tracking-wider text-primary">
            {t('scorecard.yourPlace')}
          </p>
          {card(me, true)}
          <p className="mt-2 px-1 text-[11px] font-bold uppercase tracking-wider text-foreground/50">
            {t('scorecard.ranking')}
          </p>
        </>
      )}
      {climbers.slice(0, shown).map((c) => card(c))}
      {climbers.length > shown && (
        <Button variant="flat" className="self-center" onPress={() => setShown((n) => n + PAGE)}>
          {t('scorecard.showMore', { count: climbers.length - shown })}
        </Button>
      )}
    </div>
  )
}

function ClimberCard({
  id,
  climber,
  scoringType,
  routes,
  roundName,
  mine,
  isOpen,
  onToggle,
  renderClimber,
  onOpenClimber,
  onOpenRoute,
}: {
  id?: string
  climber: ScorecardClimber
  scoringType: string
  routes: ScorecardRoute[]
  roundName: string | null
  mine: boolean
  isOpen: boolean
  onToggle: () => void
  renderClimber: (climber: ScorecardClimber) => ReactNode
  onOpenClimber?: (climber: ScorecardClimber) => void
  onOpenRoute: (routeId: string) => void
}) {
  const { t } = useTranslation()
  const glyph = useMarkGlyph()
  const topped = routes.filter((r) => climber.marks[r.routeId])
  const flashes = topped.filter((r) => climber.marks[r.routeId] === 'flash').length
  const subtotal = pointsOver(routes, climber)

  return (
    <Card
      id={id}
      shadow="none"
      className={`scroll-mt-40 border ${mine ? 'border-primary bg-primary-50' : 'border-divider'}`}
    >
      <CardBody className="gap-2.5 p-3">
        <Button
          variant="light"
          disableRipple
          aria-expanded={isOpen}
          onPress={onToggle}
          className="h-auto w-full justify-start gap-2.5 whitespace-normal px-0 text-left data-[hover=true]:bg-transparent"
        >
          <span
            className={`w-7 shrink-0 text-center text-lg font-bold tabular-nums ${
              climber.rank === 1 ? 'text-primary' : ''
            }`}
          >
            {climber.rank}
          </span>
          <span className="min-w-0 flex-1">
            {renderClimber(climber)}
            <span className="mt-0.5 block text-xs text-foreground/55">
              {t('scorecard.topsCount', { count: topped.length })}
              {scoringType !== 'tops_count' &&
                ` · ${t('scorecard.flashesCount', { count: flashes })}`}
            </span>
          </span>
          <span className="shrink-0 text-right tabular-nums">
            <span className="block text-xl font-bold leading-none">
              {formatPoints(climber.totalScore)}
            </span>
            {roundName && (
              <span className="mt-1 block text-[11px] text-foreground/55">
                {formatPoints(subtotal)}
              </span>
            )}
          </span>
          <ChevronRight
            className={`size-4 shrink-0 text-foreground/40 transition-transform ${isOpen ? 'rotate-90' : ''}`}
            aria-hidden="true"
          />
        </Button>

        <div className="grid grid-cols-[repeat(auto-fill,minmax(38px,1fr))] gap-1.5">
          {routes.map((route) => {
            const mark = climber.marks[route.routeId]
            const label = mark
              ? t(mark === 'flash' ? 'scorecard.markFlash' : 'scorecard.markTop', {
                  label: route.label,
                  points: formatPoints(markPoints(route, mark)),
                })
              : t('scorecard.markNone', { label: route.label })
            return (
              <Button
                key={route.routeId}
                isIconOnly
                disableRipple
                aria-label={label}
                title={label}
                onPress={() => onOpenRoute(route.routeId)}
                className={`relative h-9 w-full min-w-0 overflow-hidden rounded-lg border-[1.5px] text-xs font-semibold tabular-nums ${
                  mark ? MARK_STYLE[mark] : `bg-transparent ${MARK_STYLE.none}`
                }`}
              >
                {route.color && (
                  <span
                    className="absolute inset-y-0 left-0 w-1"
                    style={{ backgroundColor: route.color }}
                    aria-hidden="true"
                  />
                )}
                {route.label}
                {mark && (
                  <span
                    className="absolute right-1 top-0.5 text-[9px] font-bold leading-none"
                    aria-hidden="true"
                  >
                    {glyph(mark)}
                  </span>
                )}
              </Button>
            )
          })}
        </div>

        {isOpen && (
          <div className="flex flex-col border-t border-dashed border-divider pt-2 text-sm tabular-nums">
            {topped.length === 0 ? (
              <p className="py-1 text-foreground/55">{t('scorecard.noTopsRound')}</p>
            ) : (
              topped.map((route) => {
                const mark = climber.marks[route.routeId]
                return (
                  <div key={route.routeId} className="flex justify-between gap-2 py-1">
                    <span className="text-foreground/60">
                      <span className="font-semibold text-foreground">#{route.label}</span>
                      {' · '}
                      {t('scorecard.topsCount', { count: route.tops })}
                      {mark === 'flash' && ` · ${t('scorecard.legendFlash')}`}
                    </span>
                    <span>{formatPoints(markPoints(route, mark))}</span>
                  </div>
                )
              })
            )}
            <div className="mt-1 flex justify-between border-t border-divider pt-2 font-bold">
              <span>{roundName ?? t('scorecard.total')}</span>
              <span>{formatPoints(subtotal)}</span>
            </div>
            {onOpenClimber && (
              <Button
                size="sm"
                variant="flat"
                className="mt-2 self-start"
                onPress={() => onOpenClimber(climber)}
              >
                {t('scorecard.viewProfile')}
              </Button>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  )
}
